import React, {
  useImperativeHandle,
  forwardRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { Table, Input, Checkbox, message } from 'antd';
import { v4 as uuidv4 } from 'uuid';

// import '@style/modules/project/userGroup.scss';
import '../../../styles/modules/project/userGroup.scss';
import { projectRole } from '@model';
import useGroupNameModal from '@modules/projects/projectSetting/components/useGroupNameModal';
import GroupTitleCell from '@modules/projects/projectSetting/components/groupTitleCell';
import alert from '@components/alert';
// import { sortAtoZ } from '@utils';
import { eventDataType, eventNames } from '@utils/eventListener';
import MemberListModal from '@modules/projects/projectSetting/components/memberListModal';

function UserGroups(
  {
    initialData,
    getAllUser,
    changeUserRole,
    eventListener,
    renderAction,
    onGroupChange,
    onProjectUserChange,
  },
  ref,
) {
  const [columns, setColumns] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [groupData, setGroupData] = useState(null);
  const [projectUsers, setProjectUsers] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [memberList, setMemberList] = useState([]);

  useImperativeHandle(
    ref,
    () => ({
      getData: () => groupData,
      validate: () => {
        const emptyGroups = groupData.filter(
          (i) => i.groupMembers?.length === 0,
        );
        if (emptyGroups?.length) {
          alert.warning(
            `Please ensure that at least one user has been added to the ${emptyGroups
              .map((i) => i.groupInfo.groupName)
              .filter((i) => i)
              .map((i) => `${i} group`)
              .join(', ')} before leaving.`,
          );
        }
        return emptyGroups?.length === 0;
      },
    }),
    [groupData],
  );

  useEffect(() => {
    typeof onGroupChange === 'function' && onGroupChange(groupData);
  }, [groupData]);

  useEffect(() => {
    typeof onProjectUserChange === 'function' &&
      onProjectUserChange(projectUsers);
  }, [projectUsers]);

  const addGroup = useCallback((groupItem) => {
    setGroupData((previousGroupData) => {
      if (groupItem.id) {
        const target = previousGroupData.find(
          (i) => i.groupInfo.id === groupItem.id,
        );
        target.groupInfo.groupName = groupItem.groupName;
        if (eventListener) {
          eventListener.emit(eventNames.groupChange, 'edit', groupItem);
        }
        return [...previousGroupData];
      }
      const groupInfo = { ...groupItem, id: uuidv4() };
      if (eventListener) {
        eventListener.emit(eventNames.groupChange, 'add', groupInfo);
      }

      return [...previousGroupData, { groupInfo, groupMembers: [] }];
    });
  }, []);
  const allGroupNames = groupData
    ?.map((i) => ({
      id: i.groupInfo.id,
      groupName: i.groupInfo?.groupName,
    }))
    .concat(
      { id: 'adminRole', groupName: 'Project Admin' },
      { id: 'internal', groupName: 'All PwC' },
      { id: 'client', groupName: 'All Client' },
    );
  const { modal, open } = useGroupNameModal(addGroup, allGroupNames);

  const onRolePermissionChange = useCallback((isAdmin, user) => {
    setProjectUsers((previousProjectUsers) => {
      console.log('previousProjectUsers: ', previousProjectUsers);
      const target = previousProjectUsers.find(
        (i) => !i.isClient && i.email === user?.email,
      );
      target.permission = isAdmin
        ? projectRole.Contributor
        : projectRole['View-Only'];
      console.log('target: ', target);
      return [...previousProjectUsers];
    });
    // if (eventListener) {
    //   eventListener.emit(eventNames.pwcTeamRolePermissionChange, {
    //     isAdmin,
    //     user,
    //   });
    // }
    typeof changeUserRole === 'function' && changeUserRole(isAdmin, user);
  }, []);
  const onUserGroupChange = useCallback((add, user, groupId) => {
    setGroupData((previousGroupData) => {
      const target = previousGroupData.find((i) => i.groupInfo.id === groupId);
      const isPureClientsGroupBefore = target.groupMembers.every(
        (i) => i.isClient || i.type === 'Client',
      );
      const groupMembersEmptyBefore = target.groupMembers.length === 0;
      if (add) {
        target.groupMembers.push(
          user.isClient
            ? {
                groupId,
                client: { ...user },
                type: 'Client',
                isClient: true,
              }
            : {
                groupId,
                people: { ...user },
                type: 'Integration',
              },
        );
      } else {
        const filterClient = (i) =>
          i.type !== 'Client' || user.email !== i.client.email;
        const filterInternal = (i) =>
          i.type !== 'Integration' || user.email !== i.people.email;
        const filterFn = user.isClient ? filterClient : filterInternal;
        target.groupMembers = target.groupMembers.filter(filterFn);
      }
      const isPureClientsGroupAfter = target.groupMembers.every(
        (i) => i.isClient || i.type === 'Client',
      );
      const groupMembersEmptyAfter = target.groupMembers.length === 0;
      const groupMemberChanged =
        isPureClientsGroupBefore !== isPureClientsGroupAfter ||
        groupMembersEmptyBefore !== groupMembersEmptyAfter;
      if (groupMemberChanged && eventListener) {
        eventListener.emit(eventNames.groupClientChange, [
          {
            isPureClientGroup: isPureClientsGroupAfter,
            isEmptyGroup: groupMembersEmptyAfter,
            groupId: target.groupInfo.id,
          },
        ]);
      }

      return [...previousGroupData];
    });
  }, []);

  const getEditUsers = useCallback((previousUserData, user) => {
    const targetUserIndex = previousUserData.findIndex(
      (i) => user?.isClient === i.isClient && i.email === user.email,
    );
    if (targetUserIndex > -1) {
      previousUserData[targetUserIndex] = {
        ...previousUserData[targetUserIndex],
        ...user,
      };
    }
    return [...previousUserData];
  }, []);
  const onUserDelete = useCallback((user) => {
    setProjectUsers((previousUserData) =>
      previousUserData.filter(
        (i) => !(user?.isClient === i.isClient && i.email === user.email),
      ),
    );
    setGroupData((previousGroupData) => {
      const changedGroup = [];
      previousGroupData.forEach((groupItem) => {
        const isPureClientsGroupBefore = groupItem.groupMembers.every(
          (i) => i.isClient || i.type === 'Client',
        );
        const groupMembersEmptyBefore = groupItem.groupMembers.length === 0;
        groupItem.groupMembers = groupItem.groupMembers.filter((groupUser) =>
          user?.isClient
            ? groupUser.client?.email !== user.email
            : groupUser.people?.email !== user.email,
        );
        const isPureClientsGroupAfter = groupItem.groupMembers.every(
          (i) => i.isClient || i.type === 'Client',
        );
        const groupMembersEmptyAfter = groupItem.groupMembers.length === 0;
        if (
          isPureClientsGroupBefore !== isPureClientsGroupAfter ||
          groupMembersEmptyBefore !== groupMembersEmptyAfter
        ) {
          changedGroup.push({
            isPureClientGroup: isPureClientsGroupAfter,
            isEmptyGroup: groupMembersEmptyAfter,
            groupId: groupItem.groupInfo.id,
          });
        }
      });
      if (changedGroup.length && eventListener) {
        eventListener.emit(eventNames.groupClientChange, changedGroup);
      }
      return previousGroupData;
    });
  }, []);

  const sortProjectUsers = useCallback((users) => {
    const emptyNameUsers = users.filter((i) => !i.fullName && !i.name);
    const validNameUser = users
      .filter((i) => i.fullName || i.name)
      .sort((a, b) =>
        (a.fullName || a.name || '').localeCompare(b.fullName || b.name || ''),
      );
    return [...validNameUser, ...emptyNameUsers];
  }, []);

  const updateUser = (changedUser) => {
    setProjectUsers((users) => {
      const isAdd = !users.find(
        (i) =>
          Boolean(i.isClient) === Boolean(changedUser.isClient) &&
          i.email === changedUser.email,
      );
      let result = isAdd
        ? [
            ...(Array.isArray(changedUser) ? changedUser : [changedUser]),
            ...users,
          ]
        : getEditUsers(users, changedUser);
      result = result.filter((i) => i.email);
      result = sortProjectUsers(result);
      return result;
    });
  };

  useEffect(() => {
    if (eventListener) {
      eventListener.on(eventNames.userChange, function (type, user) {
        switch (type) {
          case eventDataType.update: {
            updateUser(user);
            break;
          }
          // case eventDataType.edit: {
          //   onUserEdit(user);
          //   break;
          // }
          case eventDataType.delete: {
            user?.email && onUserDelete(user);
            break;
          }
        }
      });
    }
  }, []);

  const copyGroupEmail = useCallback(
    (groupId) => {
      message.destroy();
      const groupMemberEmails = groupData
        .filter((i) => i.groupInfo?.id === groupId)
        ?.flatMap((i) => i.groupMembers)
        ?.map((i) => i.people?.email || i.client?.email)
        .filter((i) => Boolean(i));
      if (groupMemberEmails.length) {
        navigator.clipboard.writeText(groupMemberEmails.join(';'));
      }
      message.success('Email addresses copied!');
    },
    [groupData],
  );
  const copyAllPwCEmail = useCallback(
    (e) => {
      message.destroy();
      e.domEvent && e.domEvent.stopPropagation();
      const pwcUsersEmails = projectUsers
        .filter((i) => i.email && !i.isClient)
        .map((i) => i.email);
      if (pwcUsersEmails?.length) {
        navigator.clipboard.writeText(pwcUsersEmails.join(';'));
      }
      message.success('Email addresses copied!');
    },
    [projectUsers],
  );

  const copyAllClientEmail = useCallback(
    (e) => {
      message.destroy();
      e.domEvent && e.domEvent.stopPropagation();
      const emails = projectUsers
        .filter((i) => i.email && i.isClient)
        .map((i) => i.email);
      if (emails?.length) {
        navigator.clipboard.writeText(emails.join(';'));
      }
      message.success('Email addresses copied!');
    },
    [projectUsers],
  );
  const copyProjectContributorsEmail = useCallback(
    (e) => {
      message.destroy();
      e.domEvent && e.domEvent.stopPropagation();
      const emails = projectUsers
        .filter((i) => i.email && i.permission === projectRole.Contributor)
        .map((i) => i.email);
      if (emails?.length) {
        navigator.clipboard.writeText(emails.join(';'));
      }
      message.success('Email addresses copied!');
    },
    [projectUsers],
  );
  const showGroupMemberList = useCallback(
    (groupId) => {
      if (groupId === 'projectAdmin') {
        setMemberList(
          projectUsers.filter((i) => i.permission === projectRole.Contributor),
        );
      } else if (groupId === 'internal') {
        setMemberList(projectUsers.filter((i) => i.email && !i.isClient));
      } else if (groupId === 'client') {
        setMemberList(projectUsers.filter((i) => i.email && i.isClient));
      } else {
        const targetGroup = groupData.find((i) => i.groupInfo?.id === groupId);
        if (targetGroup?.groupMembers) {
          setMemberList(
            targetGroup.groupMembers
              .map((i) => {
                if (i.type === 'Client') {
                  return projectUsers.find(
                    (user) => user.isClient && user.email === i.client.email,
                  );
                }
                return projectUsers.find(
                  (user) => !user.isClient && user.email === i.people.email,
                );
              })
              .filter((i) => Boolean(i)),
          );
        }
      }
      setModalVisible(true);
    },
    [groupData, projectUsers],
  );

  const deleteGroup = useCallback((groupId) => {
    alert.confirm({
      content: 'Are you sure you want to delete this group?',
      okText: 'Yes',
      onOk: () => {
        setGroupData((previousGroupData) =>
          previousGroupData.filter((i) => i.groupInfo?.id !== groupId),
        );
        if (eventListener) {
          eventListener.emit(eventNames.groupChange, 'delete', { id: groupId });
        }
      },
    });
  }, []);

  useEffect(() => {
    const defaultColumns = [
      {
        dataIndex: 'email',
        title: 'Email',
        width: '18rem',
        // align: 'left',
        fixed: 'left',
        className: 'user-group-email',
        sorter: (a, b) => a.email?.localeCompare(b.email),
      },
      {
        dataIndex: 'name',
        title: 'Name',
        width: '10rem',
        // align: 'left',
        fixed: 'left',
        className: 'user-group-name',
        sorter: (a, b) =>
          (a.name || a.fullName)?.localeCompare?.(b.name || b.fullName),
        render: (_, record) => record?.name || record?.fullName,
      },
      {
        dataIndex: 'role_name',
        title: <div style={{ textAlign: 'left' }}>Project Role</div>,
        width: '6.5rem',
        className: 'project-role-name',
        fixed: 'left',
        sorter: (a, b) => a.role_name?.localeCompare?.(b.role_name),
      },
      {
        dataIndex: 'permission',
        title: (
          <GroupTitleCell
            title="Project Admin"
            onCopy={copyProjectContributorsEmail}
            showMemberList={(e) => {
              e.domEvent && e.domEvent.stopPropagation();
              showGroupMemberList('projectAdmin');
            }}
          />
        ),
        className: 'td-with-checkbox',
        width: '3.75rem',
        align: 'center',
        sorter: (a, b) =>
          (b.permission === projectRole.Contributor) -
          (a.permission === projectRole.Contributor),
        render(_, record) {
          return (
            <Checkbox
              className={record.isClient || record.nonUS ? 'read-only' : ''}
              checked={record?.permission === projectRole.Contributor}
              onChange={(e) => onRolePermissionChange(e.target.checked, record)}
            />
          );
        },
      },
      {
        dataIndex: 'pwc',
        title: (
          <GroupTitleCell
            title="All PwC"
            onCopy={copyAllPwCEmail}
            showMemberList={(e) => {
              e.domEvent && e.domEvent.stopPropagation();
              showGroupMemberList('internal');
            }}
          />
        ),
        className: 'td-with-checkbox',
        width: '3.75rem',
        align: 'center',
        sorter: (a, b) => b.isClient - a.isClient,
        render(_, record) {
          return <Checkbox className="read-only" checked={!record?.isClient} />;
        },
      },
      {
        dataIndex: 'client',
        title: (
          <GroupTitleCell
            title="All Client"
            onCopy={copyAllClientEmail}
            showMemberList={(e) => {
              e.domEvent && e.domEvent.stopPropagation();
              showGroupMemberList('client');
            }}
          />
        ),
        className: 'td-with-checkbox',
        width: '3.75rem',
        align: 'center',
        sorter: (a, b) => b.isClient - a.isClient,
        render(_, record) {
          return <Checkbox className="read-only" checked={record?.isClient} />;
        },
      },
    ];
    const groupColumns = Array.isArray(groupData)
      ? groupData.map((i) => {
          const isChecked = (record) =>
            i.groupMembers?.some(
              (member) =>
                (record.isClient &&
                  member.type === 'Client' &&
                  record.email === member.client.email) ||
                (!record.isClient &&
                  member.type === 'Integration' &&
                  record.email === member.people.email),
            );
          return {
            dataIndex: uuidv4(),
            title: (
              <GroupTitleCell
                title={i.groupInfo.groupName}
                onEdit={(e) => {
                  e.domEvent && e.domEvent.stopPropagation();
                  open({
                    mode: 'edit',
                    groupName: i.groupInfo.groupName,
                    id: i.groupInfo.id,
                  });
                }}
                onCopy={(e) => {
                  e.domEvent && e.domEvent.stopPropagation();
                  copyGroupEmail(i.groupInfo.id);
                }}
                onDelete={(e) => {
                  e.domEvent && e.domEvent.stopPropagation();
                  deleteGroup(i.groupInfo.id);
                }}
                showMemberList={(e) => {
                  e.domEvent && e.domEvent.stopPropagation();
                  showGroupMemberList(i.groupInfo.id);
                }}
              />
            ),
            width: '5.5rem',
            align: 'center',
            className: 'td-with-checkbox',
            sorter: (a, b) => isChecked(b) - isChecked(a),
            render(_, record) {
              return (
                <Checkbox
                  // Integration type user is internal user
                  checked={isChecked(record)}
                  onChange={(e) =>
                    onUserGroupChange(e.target.checked, record, i.groupInfo.id)
                  }
                />
              );
            },
          };
        })
      : [];

    setColumns([...defaultColumns, ...groupColumns]);
  }, [groupData, projectUsers]);

  const onSearch = (e) => {
    const keywords = e.target.value?.toLowerCase();
    if (keywords && keywords?.length >= 2) {
      const result = projectUsers.filter(
        (user) =>
          user.fullName?.toLowerCase().includes(keywords) ||
          user.name?.toLowerCase().includes(keywords) ||
          user?.guid?.includes(keywords) ||
          user?.email?.includes(keywords),
      );
      setSearchResult(result);
    }
    if (!keywords) {
      setSearchResult(null);
    }
  };

  useEffect(() => {
    if (Array.isArray(initialData)) {
      // generateColumns(initialData);
      setGroupData(initialData);
      getAllUser().then((users) => {
        setProjectUsers(sortProjectUsers(users));
      });
    }
  }, [initialData]);

  return (
    <div className="user-group-wrapper">
      <header>
        <Input
          className="search-box"
          placeholder="Search members by Full Name, Email or GUID"
          allowClear
          onChange={onSearch}
          prefix={<span className="appkiticon icon-search-outline" />}
        />
        <span className="add-group-btn" onClick={() => open()}>
          <span className="appkiticon icon-circle-plus-fill" />
          Add Group
        </span>
      </header>
      <Table
        className="user-group-table"
        columns={columns}
        dataSource={searchResult || projectUsers}
        pagination={false}
        rowKey={(record) => record.email + record?.guid}
        scroll={{ x: `${50 + (groupData?.length || 0) * 6}rem` }}
        showSorterTooltip={false}
      />
      {modal}
      {renderAction && renderAction()}
      <MemberListModal
        list={memberList}
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
      />
    </div>
  );
}

export default forwardRef(UserGroups);
